/**
 * AI-0 at war: when to start one, how to fight one, and when to stop.
 *
 * Still scores rather than scripts (see ai/index.ts). Every threshold is in
 * `rules.ai`: how much stronger it must be before it starts a war, the odds it will
 * attack at, how many soldiers each city keeps at home. AI-1 (Phase 8) adds the
 * strategy — fronts, stacks, siege engines, picking which city to take — and AI-2
 * (Phase 10) the diplomacy; both tune this rather than replace it.
 *
 * It has no privileged access. It attacks what it can see, marches on cities it has
 * seen, and judges a war by the same public strength figure the player is shown.
 */

import { attitude } from '../diplomacy/attitude.js';
import { carrierAt, planStrike } from '../combat/air.js';
import { resourceAccess, resourcesAt } from '../resources/access.js';
import type { Content } from '../../../content/schema/index.js';
import { apply, canApply } from '../commands/index.js';
import { weightsFor } from './personality.js';
import type { GameEvent } from '../commands/types.js';
import { cityAt, citiesOf, cityIndex } from '../cities/index.js';
import { barrage, canAttack, canDefend, isSiege, matchup } from '../combat/index.js';
import { previewMove } from '../combat/preview.js';
import {
  atWar,
  enemiesOf,
  fightingUnits,
  militaryStrength,
  offerCooldown,
  relationOf,
  rivalsOf,
  treatyLeft,
  truceLeft,
  wantsPeace,
} from '../diplomacy/index.js';
import { chance, type RngState } from '../rng/index.js';
import { neighborsByDirection } from '../state/grid.js';
import { wrappedDistance } from '../state/start.js';
import { NONE, VIS_VISIBLE, type City, type GameState, type Id, type Unit } from '../state/types.js';
import { allUnits, cargoOf, spareAirCapacity, unitDef, unitIndex, unitsOf } from '../units/index.js';
import { upgradeCost, upgradeTarget } from '../units/upgrade.js';
import { findPath, wrappedHexDistance } from '../path/astar.js';
import { capitalOf } from '../cities/economy.js';
import { isArmyType, walkable, type Front } from './campaign.js';
import { landingWater, stagingPort } from './sealift.js';
export { stagingPort } from './sealift.js';
import { appraisePeace, peaceTermsFor } from '../diplomacy/peace.js';

// ─── diplomacy ─────────────────────────────────────────────────────────────────

/** Rivals whose cities this civilisation has seen — the ones it knows where to find. */
function known(state: GameState, civId: Id): Set<Id> {
  const out = new Set<Id>();
  const memory = state.memory[civId];
  if (!memory) return out;
  for (const city of Object.values(memory.cities)) if (city.owner !== civId) out.add(city.owner);
  return out;
}

/**
 * Sue for peace where the war is going badly; start one where a rival is much weaker.
 *
 * One war at a time: AI-0 has no idea how to fight on two fronts, and declaring a
 * second war while losing the first is how civilisations die for nothing.
 */
export function directDiplomacy(state: GameState, content: Content, civId: Id, rng: RngState | undefined): GameEvent[] {
  const events: GameEvent[] = [];
  const ai = weightsFor(content, state.civs[civId]);

  // Offers waiting for an answer — only ever made to a player's seat, which the AI is
  // playing when the simulator runs with --all-ai.
  for (const enemy of enemiesOf(state, civId)) {
    const relation = relationOf(state, civId, enemy);
    if (relation?.peaceOfferedBy !== enemy) continue;
    // What the offer gives is what this side gets (ADR-0071).
    const accept = appraisePeace(state, content, civId, enemy, relation.peaceGive, relation.peaceGet).accept;
    let result = apply(state, content, { t: 'AnswerPeace', from: enemy, accept });
    // Terms that can no longer be met can only be refused.
    if (!result.ok && accept) result = apply(state, content, { t: 'AnswerPeace', from: enemy, accept: false });
    if (result.ok) events.push(...result.value);
  }

  for (const enemy of enemiesOf(state, civId)) {
    if (!wantsPeace(state, content, civId, enemy)) continue;
    if (offerCooldown(state, content, civId, enemy) > 0) continue;
    if (relationOf(state, civId, enemy)?.peaceOfferedBy === civId) continue;
    // Plain peace if the enemy would take it; otherwise terms, if anything it would
    // give is enough. A plain offer it expects refused is still made, now and then, so
    // the enemy hears that it wants peace.
    const terms = peaceTermsFor(state, content, civId, enemy) ?? { give: [], get: [] };
    const result = apply(state, content, { t: 'ProposePeace', to: enemy, give: terms.give, get: terms.get });
    if (result.ok) events.push(...result.value);
  }

  if (enemiesOf(state, civId).length > 0) return events;
  if (state.meta.turn < ai.warEarliestTurn) return events;
  if (fightingUnits(state, content, civId) < ai.warMinimumUnits) return events;

  const ours = militaryStrength(state, content, civId);
  const seen = known(state, civId);
  let target: Id | null = null;
  let weakest = Infinity;
  for (const rival of rivalsOf(state, civId)) {
    if (!seen.has(rival) || truceLeft(state, civId, rival) > 0) continue;
    const theirs = militaryStrength(state, content, rival);
    if (ours < ai.warStrengthRatio * Math.max(1, theirs)) continue;
    if (theirs < weakest) {
      weakest = theirs;
      target = rival;
    }
  }
  // A roll, so a stronger neighbour is a threat rather than a timetable. How it feels
  // about the target scales the odds, and a treaty it would break makes war rarer
  // still (ADR-0060).
  const odds =
    target === null
      ? 0
      : ai.warChancePerTurn *
        attitude(state, content, civId, target).band.warFactor *
        (treatyLeft(state, civId, target) > 0 ? ai.treatyWarFactor : 1);
  if (target !== null && rng && chance(rng, Math.min(1, odds))) {
    const result = apply(state, content, { t: 'DeclareWar', on: target });
    if (result.ok) events.push(...result.value);
  }
  return events;
}

/**
 * Whether this civilisation is arming for a war it cannot yet start (ADR-0071): at
 * peace, past `warEarliestTurn` less `warPrepLead`, with a rival it has found that it
 * is not yet `warStrengthRatio` times stronger than. Its cities then value soldiers
 * `warPrepBonus` more — a militarist a great deal, a builder hardly at all.
 */
export function arming(state: GameState, content: Content, civId: Id): boolean {
  const ai = weightsFor(content, state.civs[civId]);
  if (ai.warPrepBonus <= 0 || enemiesOf(state, civId).length > 0) return false;
  if (state.meta.turn < ai.warEarliestTurn - ai.warPrepLead) return false;
  const seen = known(state, civId);
  const ours = militaryStrength(state, content, civId);
  return rivalsOf(state, civId).some(
    (rival) => seen.has(rival) && truceLeft(state, civId, rival) === 0 && ours < ai.warStrengthRatio * Math.max(1, militaryStrength(state, content, rival)),
  );
}

// ─── garrisons ─────────────────────────────────────────────────────────────────

/**
 * Which soldier holds which city: unit id → city id.
 *
 * Each city wants `garrisonPeace` defenders, or `garrisonWar` while the civilisation is
 * at war. Units already inside are kept first, best defender first; a city still short
 * calls in the nearest soldier nobody else has claimed.
 */
export function planGarrisons(state: GameState, content: Content, civId: Id): Map<Id, Id> {
  const posts = new Map<Id, Id>();
  const soldiers = unitsOf(state, civId).filter((u) => {
    const def = unitDef(content, u);
    // Siege engines break walls; they do not man them (ADR-0064).
    return def.domain === 'land' && u.aboard === NONE && canDefend(def) && !isSiege(def);
  });
  const cities = citiesOf(state, civId);
  const short = new Map<Id, number>();

  for (const city of cities) {
    const want = garrisonWant(state, content, civId, city);
    const at = cityIndex(state.map, city);
    const inside = soldiers
      .filter((u) => unitIndex(state.map, u) === at)
      .sort((a, b) => unitDef(content, b).defence - unitDef(content, a).defence || a.id - b.id);
    const kept = inside.slice(0, want);
    for (const unit of kept) posts.set(unit.id, city.id);
    if (kept.length < want) short.set(city.id, want - kept.length);
  }

  for (const city of cities) {
    let needed = short.get(city.id) ?? 0;
    const at = cityIndex(state.map, city);
    while (needed > 0) {
      let best: Unit | null = null;
      let bestScore = Infinity;
      const manned = soldiers.some((u) => posts.get(u.id) === city.id);
      for (const unit of soldiers) {
        if (posts.has(unit.id)) continue;
        const def = unitDef(content, unit);
        // A city that has a defender calls in more defenders, not the army: attackers
        // drafted onto the walls are how a war's army melted away (ADR-0071).
        if (manned && def.attack > def.defence) continue;
        // Defenders first: a pikeman belongs on the walls, a knight in the field.
        const score = wrappedDistance(state.map, unitIndex(state.map, unit), at) * 4 + (def.attack > def.defence ? 6 : 0);
        if (score < bestScore) {
          bestScore = score;
          best = unit;
        }
      }
      if (!best) break;
      posts.set(best.id, city.id);
      needed--;
    }
  }
  return posts;
}

/**
 * How many defenders a city wants: the base garrison, plus one for every
 * `threatPerDefender` of enemy attack the civilisation can see within `threatRadius` —
 * in full from rivals at war, at `threatPeaceShare` from anyone else's soldiers. A small
 * influence map, read one city at a time: AI-0 kept one soldier everywhere and lost
 * border towns to armies it had watched march up.
 */
export function garrisonWant(state: GameState, content: Content, civId: Id, city: City): number {
  const ai = weightsFor(content, state.civs[civId]);
  const base = enemiesOf(state, civId).length > 0 ? ai.garrisonWar : ai.garrisonPeace;
  const at = cityIndex(state.map, city);
  const vision = state.vision[civId];
  let threat = 0;
  for (const unit of allUnits(state)) {
    if (unit.owner === civId || unit.aboard !== NONE) continue;
    const def = unitDef(content, unit);
    if (def.domain !== 'land' || def.attack === 0) continue;
    const there = unitIndex(state.map, unit);
    if (vision && vision[there] !== VIS_VISIBLE) continue;
    if (wrappedDistance(state.map, there, at) > ai.threatRadius) continue;
    threat += def.attack * (atWar(state, civId, unit.owner) ? 1 : ai.threatPeaceShare);
  }
  return Math.min(ai.maxGarrison, base + Math.floor(threat / ai.threatPerDefender));
}

// ─── soldiers ──────────────────────────────────────────────────────────────────

const scratch = new Int32Array(6);

/** How many of this civilisation's fighting units stand next to a tile. */
function besiegers(state: GameState, content: Content, civId: Id, target: number): number {
  neighborsByDirection(state.map, target, scratch);
  const around = new Set<number>();
  for (let d = 0; d < 6; d++) if (scratch[d]! !== -1) around.add(scratch[d]!);
  return unitsOf(state, civId).filter((u) => around.has(unitIndex(state.map, u)) && canAttack(unitDef(content, u))).length;
}

/**
 * Strike whatever is worth striking next to this unit, as long as it has the moves.
 * Takes undefended things outright; attacks at `attackMinimumOdds`, or at `siegeOdds`
 * when enough comrades are alongside to finish what it starts.
 */
function strike(state: GameState, content: Content, civId: Id, unit: Unit): GameEvent[] {
  const events: GameEvent[] = [];
  const ai = weightsFor(content, state.civs[civId]);
  for (let guard = 0; guard < 6; guard++) {
    if (!state.units[unit.id] || unit.movesLeft <= 0) break;
    const here = unitIndex(state.map, unit);
    neighborsByDirection(state.map, here, scratch);
    const tiles = [...scratch].filter((i) => i !== -1);

    let best: number | null = null;
    let bestValue = -Infinity;
    for (const to of tiles) {
      // Only what the rules would allow — a zone of control can forbid a capture.
      if (canApply(state, content, { t: 'MoveUnit', unitId: unit.id, to }) !== null) continue;
      const preview = previewMove(state, content, unit, to);
      let value = -Infinity;
      if (preview.kind === 'capture') value = 2 + (preview.city ? 1 : 0);
      else if (preview.kind === 'attack') {
        const needed = besiegers(state, content, civId, to) >= ai.siegeUnits ? ai.siegeOdds : ai.attackMinimumOdds;
        if (preview.matchup.odds >= needed) value = preview.matchup.odds;
      }
      if (value > bestValue) {
        bestValue = value;
        best = to;
      }
    }
    if (best === null || bestValue === -Infinity) break;
    const result = apply(state, content, { t: 'MoveUnit', unitId: unit.id, to: best });
    if (!result.ok) break;
    events.push(...result.value);
  }
  return events;
}

/** The nearest city of an enemy this civilisation knows the whereabouts of. */
function nearestEnemyCity(state: GameState, civId: Id, from: number, enemies: readonly Id[]): number | null {
  const memory = state.memory[civId];
  if (!memory) return null;
  let best: number | null = null;
  let bestDistance = Infinity;
  for (const [key, city] of Object.entries(memory.cities)) {
    if (!enemies.includes(city.owner)) continue;
    const index = Number(key);
    const distance = wrappedDistance(state.map, from, index);
    if (distance < bestDistance || (distance === bestDistance && best !== null && index < best)) {
      bestDistance = distance;
      best = index;
    }
  }
  return best;
}

function order(state: GameState, content: Content, cmd: Parameters<typeof apply>[2]): GameEvent[] {
  const result = apply(state, content, cmd);
  return result.ok ? result.value : [];
}

/**
 * One fighting unit's turn. Returns null when it has nothing military to do and the
 * caller should treat it as an explorer, as AI-0 always did at peace.
 */
export function directSoldier(
  state: GameState,
  content: Content,
  civId: Id,
  unit: Unit,
  posts: ReadonlyMap<Id, Id>,
  front: Front | null = null,
): GameEvent[] | null {
  const def = unitDef(content, unit);
  const enemies = enemiesOf(state, civId);
  const events: GameEvent[] = [];
  // Below decks, the ship decides. It puts them ashore where the fighting is.
  if (unit.aboard !== NONE) return events;

  if (enemies.length > 0 && canAttack(def)) events.push(...strike(state, content, civId, unit));
  if (!state.units[unit.id] || unit.movesLeft <= 0) return events;

  const post = posts.get(unit.id);
  if (post !== undefined) {
    const city = state.cities[post];
    if (city) {
      const at = cityIndex(state.map, city);
      if (unitIndex(state.map, unit) === at) {
        if (unit.orders?.kind !== 'fortify') events.push(...order(state, content, { t: 'Fortify', unitId: unit.id }));
      } else if (!(unit.orders?.kind === 'goto' && unit.orders.destination === at)) {
        events.push(...order(state, content, { t: 'SetGoto', unitId: unit.id, to: at }));
      }
      return events;
    }
  }

  const here = unitIndex(state.map, unit);
  // A siege engine at peace waits in the nearest city rather than wandering off to explore.
  if (enemies.length === 0 && isSiege(def)) {
    const home = citiesOf(state, civId)
      .map((c) => cityIndex(state.map, c))
      .sort((a, b) => wrappedDistance(state.map, here, a) - wrappedDistance(state.map, here, b) || a - b)[0];
    if (home === undefined) return events;
    if (home === here) {
      if (unit.orders?.kind !== 'fortify') events.push(...order(state, content, { t: 'Fortify', unitId: unit.id }));
    } else if (!(unit.orders?.kind === 'goto' && unit.orders.destination === home)) {
      events.push(...order(state, content, { t: 'SetGoto', unitId: unit.id, to: home }));
    }
    return events;
  }
  if (enemies.length === 0) return events.length > 0 ? events : null;

  // The army of a war with a plan (ADR-0071): gather at home, then march together.
  if (front && isArmyType(def)) {
    const army = campaignMove(state, content, civId, unit, front);
    if (army !== null) return [...events, ...army];
  } else if (front && def.domain === 'land') {
    // A defender with no post, in a planned war, mans the nearest wall.
    const home = citiesOf(state, civId)
      .map((c) => cityIndex(state.map, c))
      .sort((a, b) => wrappedDistance(state.map, here, a) - wrappedDistance(state.map, here, b) || a - b)[0];
    if (home === undefined) return events.length > 0 ? events : null;
    if (home === here) return unit.orders?.kind === 'fortify' ? events : [...events, ...order(state, content, { t: 'Fortify', unitId: unit.id })];
    if (unit.orders?.kind === 'goto' && unit.orders.destination === home) return events;
    return [...events, ...order(state, content, { t: 'SetGoto', unitId: unit.id, to: home })];
  }
  if (unit.orders?.kind === 'goto') return events;

  if (!canAttack(def)) {
    // A defender with no post at war goes home to the nearest city.
    const home = citiesOf(state, civId)
      .map((c) => cityIndex(state.map, c))
      .sort((a, b) => wrappedDistance(state.map, here, a) - wrappedDistance(state.map, here, b) || a - b)[0];
    if (home === undefined || home === here) return events.length > 0 ? events : null;
    events.push(...order(state, content, { t: 'SetGoto', unitId: unit.id, to: home }));
    return events;
  }

  const target = nearestEnemyCity(state, civId, here, enemies);
  if (target === null) return events.length > 0 ? events : null;
  const march = apply(state, content, { t: 'SetGoto', unitId: unit.id, to: target });
  if (march.ok) events.push(...march.value);
  else if (march.error === 'NoPath') {
    // Overseas. Go to the port the invasion sails from, and board when a ship is in.
    const port = stagingPort(state, content, civId, target);
    if (port === null) return events.length > 0 ? events : null;
    if (port === here) {
      const board = apply(state, content, { t: 'Board', unitId: unit.id });
      if (board.ok) events.push(...board.value);
      else if (unit.orders?.kind !== 'fortify') events.push(...order(state, content, { t: 'Fortify', unitId: unit.id }));
    } else {
      events.push(...order(state, content, { t: 'SetGoto', unitId: unit.id, to: port }));
    }
  }
  // Already beside it, and the odds were not good enough: dig in and wait for company.
  else if (unit.orders?.kind !== 'fortify') events.push(...order(state, content, { t: 'Fortify', unitId: unit.id }));
  return events;
}

/**
 * One soldier's part in a planned war. In the field: keep with the column — wait,
 * dug in, when more than `marchSpread` tiles ahead of the hindmost; dig in beside the
 * target, where `strike` will attack once enough comrades are alongside. Otherwise go
 * to the staging city and wait there. Null when the march has no road, so the caller
 * falls back to the old way.
 */
function campaignMove(state: GameState, content: Content, civId: Id, unit: Unit, front: Front): GameEvent[] | null {
  const ai = weightsFor(content, state.civs[civId]);
  const here = unitIndex(state.map, unit);
  const fortify = (): GameEvent[] => (unit.orders?.kind === 'fortify' ? [] : order(state, content, { t: 'Fortify', unitId: unit.id }));
  const goTo = (to: number): GameEvent[] | null => {
    if (unit.orders?.kind === 'goto' && unit.orders.destination === to) return [];
    const result = apply(state, content, { t: 'SetGoto', unitId: unit.id, to });
    return result.ok ? result.value : null;
  };
  if (front.overseas) {
    // Ashore beside the target: fight as on land. Otherwise to the port, and aboard.
    if (front.field.has(unit.id) && walkable(state, content, civId, here, front.target)) {
      const d = wrappedDistance(state.map, here, front.target);
      return d <= 1 ? fortify() : goTo(front.target);
    }
    if (here !== front.staging) return goTo(front.staging);
    if (front.field.has(unit.id)) {
      const board = apply(state, content, { t: 'Board', unitId: unit.id });
      if (board.ok) return board.value;
    }
    return fortify();
  }
  if (front.field.has(unit.id)) {
    const d = wrappedDistance(state.map, here, front.target);
    if (d <= 1) return fortify();
    if (d < front.rear - ai.marchSpread) return fortify();
    return goTo(front.target);
  }
  if (here === front.staging) return fortify();
  return goTo(front.staging);
}

// ─── bombardment (ADR-0064) ────────────────────────────────────────────────────

/**
 * Fire this unit's guns at the enemy it would hurt most within range — a city's
 * defenders counting half again, since that is where an assault goes next. Nothing when
 * there is no war, no one in range, or nothing left to hurt.
 */
export function bombardBest(state: GameState, content: Content, civId: Id, unit: Unit): GameEvent[] {
  const def = unitDef(content, unit);
  if (!def.bombard || unit.movesLeft <= 0 || unit.aboard !== NONE) return [];
  const enemies = enemiesOf(state, civId);
  if (enemies.length === 0) return [];
  const here = unitIndex(state.map, unit);
  const vision = state.vision[civId];
  const tiles = new Set<number>();
  for (const other of allUnits(state)) {
    if (!enemies.includes(other.owner) || other.aboard !== NONE) continue;
    const at = unitIndex(state.map, other);
    if (vision?.[at] !== VIS_VISIBLE) continue;
    const distance = wrappedHexDistance(state.map, here, at);
    if (distance >= 1 && distance <= def.bombard.range) tiles.add(at);
  }
  let best: number | null = null;
  let bestValue = 0;
  for (const at of [...tiles].sort((a, b) => a - b)) {
    if (canApply(state, content, { t: 'Bombard', unitId: unit.id, target: at }) !== null) continue;
    const shot = barrage(state, content, unit, at);
    if (!shot) continue;
    const value = shot.expected * (cityAt(state, state.map, at) ? 1.5 : 1);
    if (value > bestValue) {
      bestValue = value;
      best = at;
    }
  }
  return best === null ? [] : order(state, content, { t: 'Bombard', unitId: unit.id, target: best });
}

// ─── aircraft (ADR-0065) ───────────────────────────────────────────────────────

/**
 * An aircraft's turn: strike the enemy within range it has the best chance against,
 * if that chance is at least `attackMinimumOdds` — a city's defenders preferred at equal
 * odds — or else, at war, rebase to whichever of its cities within range is nearest the
 * enemy, if that is two tiles nearer than where it is. Interceptors guard wherever they
 * are; interception costs no move.
 */
export function directAircraft(state: GameState, content: Content, civId: Id, unit: Unit): GameEvent[] {
  const def = unitDef(content, unit);
  if (unit.movesLeft <= 0 || def.airRange === null) return [];
  const enemies = enemiesOf(state, civId);
  if (enemies.length === 0) return [];
  const ai = weightsFor(content, state.civs[civId]);
  const here = unitIndex(state.map, unit);
  const vision = state.vision[civId];

  const tiles = new Set<number>();
  for (const other of allUnits(state)) {
    if (!enemies.includes(other.owner)) continue;
    const at = unitIndex(state.map, other);
    if (vision?.[at] !== VIS_VISIBLE) continue;
    const distance = wrappedHexDistance(state.map, here, at);
    if (distance >= 1 && distance <= def.airRange) tiles.add(at);
  }
  let best: number | null = null;
  let bestValue = -Infinity;
  for (const at of [...tiles].sort((a, b) => a - b)) {
    if (canApply(state, content, { t: 'AirMission', unitId: unit.id, target: at }) !== null) continue;
    const plan = planStrike(state, content, unit, at);
    if (!plan || plan.odds < ai.attackMinimumOdds) continue;
    const value = plan.odds + (cityAt(state, state.map, at) ? 0.05 : 0);
    if (value > bestValue) {
      bestValue = value;
      best = at;
    }
  }
  if (best !== null) return order(state, content, { t: 'AirMission', unitId: unit.id, target: best });

  // A carrier in port bound for an overseas war: go aboard (ADR-0066).
  if (unit.aboard === NONE && overseasTarget(state, content, civId) !== null) {
    const deck = carrierAt(state, content, civId, here);
    if (deck) return order(state, content, { t: 'Board', unitId: unit.id });
  }

  // Nothing worth striking: move nearer the war — to a city, or a carrier with room.
  const target = nearestEnemyCity(state, civId, here, enemies);
  if (target === null) return [];
  const now = wrappedDistance(state.map, here, target);
  let base: number | null = null;
  let baseDistance = now - 1;
  const bases = citiesOf(state, civId).map((c) => cityIndex(state.map, c));
  for (const other of unitsOf(state, civId)) {
    if (other.id !== unit.aboard && unitDef(content, other).airCapacity > 0 && spareAirCapacity(state, content, other) > 0) bases.push(unitIndex(state.map, other));
  }
  for (const at of bases) {
    const reach = wrappedHexDistance(state.map, here, at);
    if (reach < 1 || reach > def.airRange) continue;
    const d = wrappedDistance(state.map, at, target);
    if (d < baseDistance || (d === baseDistance && base !== null && at < base)) {
      baseDistance = d;
      base = at;
    }
  }
  return base === null ? [] : order(state, content, { t: 'AirMission', unitId: unit.id, target: base });
}

// ─── warships (ADR-0067) ────────────────────────────────────────────────────────

/**
 * A warship's turn, after any attack it could make: keep escorting the ship it guards;
 * or take on a convoy — a transport or carrier with cargo or at sea — that nobody
 * guards; or, at war, go after an enemy ship within `huntRadius` it can beat; or, at
 * war, guard the nearest port. Null at peace with nothing to guard: the caller lets it
 * explore, as before.
 */
export function directWarship(state: GameState, content: Content, civId: Id, ship: Unit): GameEvent[] | null {
  const here = unitIndex(state.map, ship);
  if (ship.orders?.kind === 'escort' && state.units[ship.orders.target]) return [];

  const guarded = new Set<number>();
  for (const unit of unitsOf(state, civId)) if (unit.orders?.kind === 'escort') guarded.add(unit.orders.target);
  const convoys = unitsOf(state, civId)
    .filter((u) => {
      const d = unitDef(content, u);
      if (d.domain !== 'sea' || (d.capacity === 0 && d.airCapacity === 0) || guarded.has(u.id)) return false;
      return cargoOf(state, u).length > 0 || content.terrain.get(state.map.terrain[unitIndex(state.map, u)]!).water;
    })
    .sort((a, b) => wrappedDistance(state.map, here, unitIndex(state.map, a)) - wrappedDistance(state.map, here, unitIndex(state.map, b)) || a.id - b.id);
  for (const convoy of convoys) {
    const result = apply(state, content, { t: 'Escort', unitId: ship.id, target: convoy.id });
    if (result.ok) return result.value;
  }

  const enemies = enemiesOf(state, civId);
  if (enemies.length === 0) return null;
  const ai = weightsFor(content, state.civs[civId]);
  const vision = state.vision[civId];
  let prey: number | null = null;
  let best = -1;
  for (const other of allUnits(state)) {
    if (!enemies.includes(other.owner) || other.aboard !== NONE || unitDef(content, other).domain !== 'sea') continue;
    const at = unitIndex(state.map, other);
    if (vision?.[at] !== VIS_VISIBLE || wrappedDistance(state.map, here, at) > ai.huntRadius) continue;
    const fight = matchup(state, content, ship, at);
    if (!fight || fight.odds < ai.attackMinimumOdds || fight.odds <= best) continue;
    best = fight.odds;
    prey = at;
  }
  if (prey !== null) {
    if (ship.orders?.kind === 'goto' && ship.orders.destination === prey) return [];
    const chase = apply(state, content, { t: 'SetGoto', unitId: ship.id, to: prey });
    if (chase.ok) return chase.value;
  }

  const home = stagingPort(state, content, civId, here);
  if (home === null) return [];
  if (home === here) return ship.orders?.kind === 'fortify' ? [] : order(state, content, { t: 'Fortify', unitId: ship.id });
  if (ship.orders?.kind === 'goto' && ship.orders.destination === home) return [];
  return order(state, content, { t: 'SetGoto', unitId: ship.id, to: home });
}

// ─── aircraft carriers (ADR-0066) ──────────────────────────────────────────────

/**
 * A carrier's turn. With an overseas war it waits in the staging port until planes are
 * aboard (two, or a full deck, or none left to come), then sails to open water about
 * `carrierStandoff` tiles from the enemy city and holds there while its planes strike.
 * At peace it brings its planes home to the nearest port.
 */
export function directCarrier(state: GameState, content: Content, civId: Id, ship: Unit): GameEvent[] {
  const def = unitDef(content, ship);
  const events: GameEvent[] = [];
  if (ship.movesLeft <= 0) return events;
  const here = unitIndex(state.map, ship);
  const planes = cargoOf(state, ship).filter((u) => unitDef(content, u).domain === 'air').length;
  const goto = (to: number): GameEvent[] =>
    ship.orders?.kind === 'goto' && ship.orders.destination === to ? [] : order(state, content, { t: 'SetGoto', unitId: ship.id, to });
  const target = overseasTarget(state, content, civId);

  if (target === null) {
    const home = stagingPort(state, content, civId, here);
    if (home === null || home === here) return events;
    return goto(home);
  }

  const port = stagingPort(state, content, civId, target);
  const ashore = unitsOf(state, civId).filter((u) => unitDef(content, u).domain === 'air' && u.aboard === NONE).length;
  const loaded = planes >= Math.min(2, def.airCapacity) || (planes > 0 && ashore === 0);
  if (!loaded) {
    if (port === null || port === here) return events;
    return goto(port);
  }

  // At sea within standoff of the target already: hold, and let the planes work.
  const ai = weightsFor(content, state.civs[civId]);
  const distance = wrappedHexDistance(state.map, here, target);
  const atSea = content.terrain.get(state.map.terrain[here]!).water;
  if (atSea && distance <= ai.carrierStandoff + 1) return events;

  // Otherwise the nearest open water within standoff that a route reaches.
  const candidates: number[] = [];
  const n = state.map.width * state.map.height;
  for (let i = 0; i < n; i++) {
    if (!content.terrain.get(state.map.terrain[i]!).water) continue;
    const d = wrappedHexDistance(state.map, i, target);
    if (d >= 2 && d <= ai.carrierStandoff) candidates.push(i);
  }
  candidates.sort((a, b) => wrappedDistance(state.map, here, a) - wrappedDistance(state.map, here, b) || a - b);
  for (const station of candidates.slice(0, 4)) {
    if (canApply(state, content, { t: 'SetGoto', unitId: ship.id, to: station }) === null) return goto(station);
  }
  return events;
}

// ─── upgrades ──────────────────────────────────────────────────────────────────

/**
 * Modernise what stands in its cities, oldest first, while the treasury keeps
 * `upgradeReserve` in hand. Without this AI-0's armies stayed bronze-age for good.
 */
export function directUpgrades(state: GameState, content: Content, civId: Id): GameEvent[] {
  const events: GameEvent[] = [];
  const civ = state.civs[civId];
  if (!civ) return events;
  const access = resourceAccess(state, content, civId);
  for (const unit of unitsOf(state, civId)) {
    const has = resourcesAt(state, access, unitIndex(state.map, unit));
    const target = upgradeTarget(content, civ, unitDef(content, unit), has);
    if (!target) continue;
    const cost = upgradeCost(content, unitDef(content, unit), target);
    if (civ.treasury - cost < weightsFor(content, civ).upgradeReserve) continue;
    const result = apply(state, content, { t: 'UpgradeUnit', unitId: unit.id });
    if (result.ok) events.push(...result.value);
  }
  return events;
}

/** Gold it would take to upgrade the cheapest unit that could be upgraded, or 0. */
export function upgradeBill(state: GameState, content: Content, civId: Id): number {
  const civ = state.civs[civId];
  if (!civ) return 0;
  let cheapest = 0;
  for (const unit of unitsOf(state, civId)) {
    const from = unitDef(content, unit);
    const target = upgradeTarget(content, civ, from);
    if (!target) continue;
    const cost = upgradeCost(content, from, target);
    if (cheapest === 0 || cost < cheapest) cheapest = cost;
  }
  return cheapest;
}

// ─── the sea ───────────────────────────────────────────────────────────────────

/**
 * The enemy city a war is aimed at, when there is no way to it on foot — the case
 * that needs ships. Null when the nearest enemy city can be walked to, or there is no
 * war, or nothing is known of the enemy's cities.
 */
export function overseasTarget(state: GameState, content: Content, civId: Id): number | null {
  const enemies = enemiesOf(state, civId);
  if (enemies.length === 0) return null;
  const capital = capitalOf(state, content, civId);
  if (!capital) return null;
  const from = cityIndex(state.map, capital);
  const target = nearestEnemyCity(state, civId, from, enemies);
  if (target === null) return null;
  const probe = content.units.all.find((u) => u.domain === 'land' && u.attack > 0);
  if (!probe) return null;
  const walk = findPath({ content, map: state.map, def: probe, vision: state.vision[civId] ?? null }, from, target);
  return walk === null ? target : null;
}



/**
 * A ship's turn, or null to let it explore.
 *
 * AI-0's navy does three things: a warship strikes what it can beat; a carrier ferries
 * an army from the staging port to the enemy's shore and puts it ashore; anything
 * else explores, which is how island civilisations finally meet each other.
 */
/** Sail a loaded ship to water beside the target and put everyone ashore nearest it. */
function sailAndLand(state: GameState, content: Content, ship: Unit, target: number): GameEvent[] {
  const events: GameEvent[] = [];
  const here = unitIndex(state.map, ship);
  const landing = landingWater(state, content, ship, target);
  if (landing === null) return events;
  if (landing !== here) {
    if (!(ship.orders?.kind === 'goto' && ship.orders.destination === landing)) {
      events.push(...order(state, content, { t: 'SetGoto', unitId: ship.id, to: landing }));
    }
    if (unitIndex(state.map, ship) !== landing) return events;
  }
  // Off the boats: each soldier onto the free shore nearest the target.
  for (const unit of cargoOf(state, ship)) {
    const at = unitIndex(state.map, unit);
    const ring = new Int32Array(6);
    neighborsByDirection(state.map, at, ring);
    const shore = [...ring]
      .filter((i) => i !== -1 && canApply(state, content, { t: 'MoveUnit', unitId: unit.id, to: i }) === null)
      .filter((i) => !content.terrain.get(state.map.terrain[i]!).water)
      .sort((a, b) => wrappedDistance(state.map, a, target) - wrappedDistance(state.map, b, target) || a - b);
    if (shore[0] !== undefined) events.push(...order(state, content, { t: 'MoveUnit', unitId: unit.id, to: shore[0] }));
  }
  return events;
}

export function directShip(state: GameState, content: Content, civId: Id, ship: Unit, front: Front | null = null): GameEvent[] | null {
  const def = unitDef(content, ship);
  const events: GameEvent[] = [];
  const enemies = enemiesOf(state, civId);
  // A carrier keeps out of gunfights; its planes do the fighting (ADR-0066).
  if (def.airCapacity > 0) return directCarrier(state, content, civId, ship);
  if (enemies.length > 0 && canAttack(def)) events.push(...strike(state, content, civId, ship));
  if (!state.units[ship.id] || ship.movesLeft <= 0) return events;
  if (def.capacity === 0 && canAttack(def)) {
    const duty = directWarship(state, content, civId, ship);
    if (duty !== null) return [...events, ...duty];
  }
  if (def.capacity === 0) return events.length > 0 ? events : null;

  const here = unitIndex(state.map, ship);
  const cargo = cargoOf(state, ship);
  // A planned invasion (ADR-0072): wait in port until the army is aboard, then all sail.
  if (front && front.overseas) {
    if (landingWater(state, content, ship, front.target) === null) return events.length > 0 ? events : null;
    const port = front.staging;
    const boarding = front.marching && [...front.field].some((id) => {
      const u = state.units[id];
      return u !== undefined && u.aboard === NONE && unitIndex(state.map, u) === port;
    });
    if (cargo.length > 0 && !(here === port && boarding && cargo.length < def.capacity)) {
      return [...events, ...sailAndLand(state, content, ship, front.target)];
    }
    if (here !== port && !(ship.orders?.kind === 'goto' && ship.orders.destination === port)) {
      events.push(...order(state, content, { t: 'SetGoto', unitId: ship.id, to: port }));
    }
    return events;
  }
  const target = overseasTarget(state, content, civId);

  if (target === null) {
    // No invasion to carry. Anyone aboard goes home and ashore.
    if (cargo.length === 0) return events.length > 0 ? events : null;
    const home = stagingPort(state, content, civId, here);
    if (home === null) return events;
    if (home === here) {
      for (const unit of cargo) events.push(...order(state, content, { t: 'Unload', unitId: unit.id }));
    } else if (!(ship.orders?.kind === 'goto' && ship.orders.destination === home)) {
      events.push(...order(state, content, { t: 'SetGoto', unitId: ship.id, to: home }));
    }
    return events;
  }

  const port = stagingPort(state, content, civId, target);
  const full = cargo.length >= def.capacity;
  const waiting = port === here && cargo.length > 0 && !soldiersComing(state, content, civId, port);

  if (cargo.length > 0 && (full || waiting || port !== here)) {
    return [...events, ...sailAndLand(state, content, ship, target)];
  }

  // Empty: back to the staging port to take on soldiers.
  if (port === null) return events.length > 0 ? events : null;
  if (port !== here && !(ship.orders?.kind === 'goto' && ship.orders.destination === port)) {
    events.push(...order(state, content, { t: 'SetGoto', unitId: ship.id, to: port }));
  }
  return events;
}

/** Whether soldiers are still on their way to a port to board. */
function soldiersComing(state: GameState, content: Content, civId: Id, port: number): boolean {
  return unitsOf(state, civId).some(
    (u) => u.aboard === NONE && u.orders?.kind === 'goto' && u.orders.destination === port && canAttack(unitDef(content, u)),
  );
}
